import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type Keycloak from 'keycloak-js';
import type { KeycloakProfile } from 'keycloak-js';
import { config } from '~/config';
import keycloakInstance, { initKeycloakOnce } from '~/util/keycloak-instance';
import { setSentryUser, clearSentryUser } from '~/config/sentry';

interface AuthContextType {
  keycloak: Keycloak | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  user: KeycloakProfile | null;
  token: string | null;
  login: () => void;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<KeycloakProfile | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if Keycloak config is valid
    if (!config.keycloak.url || !config.keycloak.realm || !config.keycloak.clientId) {
      console.error('Keycloak config is missing. Please check your environment variables.');
      setError('Configuração do Keycloak não encontrada. Verifique as variáveis de ambiente.');
      setIsLoading(false);
      return;
    }

    let intervalId: NodeJS.Timeout | null = null;
    let cancelled = false;

    console.log('🔐 Initializing Keycloak...');

    initKeycloakOnce()
      .then(async (authenticated) => {
        if (cancelled) {
          return;
        }
        console.log('✅ Keycloak initialized. Authenticated:', authenticated);

        setKeycloak(keycloakInstance);
        setIsAuthenticated(authenticated);
        setToken(keycloakInstance.token || null);

        if (!authenticated) {
          return;
        }

        try {
          const profile = await keycloakInstance.loadUserProfile();
          if (cancelled) {
            return;
          }
          setUser(profile);
          console.log('👤 User profile loaded:', profile.username);
          setSentryUser(profile);
        } catch (profileError) {
          console.error('Erro ao carregar perfil do usuário:', profileError);
        }

        if (cancelled) {
          return;
        }

        intervalId = setInterval(() => {
          keycloakInstance
            .updateToken(70)
            .then((refreshed) => {
              if (refreshed) {
                setToken(keycloakInstance.token || null);
                console.log('🔄 Token atualizado');
              }
            })
            .catch(() => {
              console.error('❌ Falha ao atualizar token');
            });
        }, 60000);
      })
      .catch((initError: unknown) => {
        if (cancelled) {
          return;
        }
        console.error('❌ Erro ao inicializar Keycloak:', initError);
        const message = initError instanceof Error ? initError.message : String(initError);
        setError('Erro ao conectar com Keycloak: ' + message);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
      console.log('🧹 Cleaning up Keycloak...');
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, []);

  const login = () => {
    keycloak?.login();
  };

  const logout = () => {
    clearSentryUser();
    keycloak?.logout({
      redirectUri: window.location.origin,
    });
  };

  const refreshToken = async (): Promise<boolean> => {
    if (!keycloak) return false;

    try {
      const refreshed = await keycloak.updateToken(5);
      if (refreshed) {
        setToken(keycloak.token || null);
      }
      return true;
    } catch {
      return false;
    }
  };

  const hasRole = (role: string): boolean => {
    if (!keycloak) return false;
    return keycloak.hasRealmRole(role);
  };

  const value: AuthContextType = {
    keycloak,
    isAuthenticated,
    isLoading,
    user,
    token,
    login,
    logout,
    refreshToken,
    hasRole,
  };

  // Show error message if configuration is invalid
  if (error && !isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-red-50">
        <div className="text-center p-6 bg-white rounded-lg shadow-lg">
          <h2 className="text-xl font-bold text-red-600 mb-2">Erro de Configuração</h2>
          <p className="text-gray-700">{error}</p>
          <p className="text-sm text-gray-500 mt-4">
            Verifique se as variáveis de ambiente estão configuradas corretamente.
          </p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}

export const PERMISSIONS = {
  QUESTIONS_WRITE: 'questions:write',
  TAXONOMIES_MANAGE: 'taxonomies:manage',
  COURSES_WRITE: 'courses:write',
  COURSES_EDITOR: 'courses:editor',
  COURSES_MODULES_WRITE: 'courses:modules:write',
  COURSES_TEACHERS_WRITE: 'courses:teachers:write',
  USERS_WRITE: 'users:write',
  COUPONS_WRITE: 'coupons:write',
  DASHBOARDS_PURCHASES_READ: 'dashboards:purchases:read',
  DASHBOARDS_ENGAGEMENTS_READ: 'dashboards:engagements:read',
  DASHBOARDS_CONTENTS_READ: 'dashboards:contents:read',
} as const;
